import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ScrollReveal } from '../hooks/useScrollAnimation';
import { 
  Zap, Battery, MapPin, Smartphone, CreditCard, Clock, 
  ChevronRight, Building2, Users, Leaf, ArrowRight,
  Shield, Globe, Gauge, Download, QrCode, Wallet, Plug,
  Server, Check, BarChart3, Truck, Store, Hotel, TreePine,
  ChevronDown, Plus, Minus, ChevronLeft, Play
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [stats, setStats] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Auto-slide every 6 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 6000);
    return () => clearInterval(timer);
  }, []);
  
  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);
  
  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % 3);
  }, []);
  
  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + 3) % 3);
  }, []);
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);
  
  const features = [
    {
      icon: Zap,
      title: 'Karikim Ultra-Fast',
      description: 'Mbi 240 kW fuqi karikimi. Karikoni automjetin tuaj në vetëm 15-20 minuta.',
      color: 'bg-lime-100 text-lime-700'
    },
    {
      icon: MapPin,
      title: 'Rrjet i Gjerë',
      description: 'Stacione karikimi në të gjithë Shqipërinë, në qytete kryesore dhe autostrada.',
      color: 'bg-blue-100 text-blue-700'
    },
    {
      icon: Smartphone,
      title: 'Aplikacion Mobile',
      description: 'Gjeni stacionet, nisni karikimin dhe paguani direkt nga aplikacioni.',
      color: 'bg-indigo-100 text-indigo-700'
    },
    {
      icon: CreditCard,
      title: 'Pagesa e Thjeshtë',
      description: 'Paguani me kartë, aplikacion ose RFID. Fatura transparente dhe të detajuara.',
      color: 'bg-purple-100 text-purple-700'
    },
    {
      icon: Building2,
      title: 'Zgjidhje Biznesi',
      description: 'Paketa speciale për kompani dhe menaxhim flotash automjetesh elektrike.',
      color: 'bg-orange-100 text-orange-700'
    },
    {
      icon: Leaf,
      title: 'Energji e Pastër',
      description: 'Kontribuoni në një të ardhme më të gjelbër me energji 100% të rinovueshme.',
      color: 'bg-green-100 text-green-700'
    }
  ];
  
  const chargerTypes = [
    { power: '22 kW', type: 'AC Normal', time: '3-4 orë', icon: Battery },
    { power: '60 kW', type: 'DC Fast', time: '45 min', icon: Zap },
    { power: '120 kW', type: 'DC Fast+', time: '25 min', icon: Gauge },
    { power: '240 kW', type: 'Ultra-Fast', time: '15 min', icon: Zap }
  ];
  
  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Slider Section */}
      <section 
        data-testid="hero-section"
        className="relative min-h-[80vh] lg:min-h-[85vh] flex items-center overflow-hidden"
      >
        {/* Slides Container */}
        <div className="absolute inset-0">
          {/* Slide 1 - Karikim i Shpejtë */}
          <div 
            className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 0 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}
          >
            <div 
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: 'url(/images/app-section-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
          
          {/* Slide 2 - Shkarko Aplikacionin */}
          <div 
            className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 1 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            style={{ background: 'linear-gradient(135deg, #b0d33a 0%, #8fb82e 50%, #6a9a1f 100%)' }}
          >
            <div 
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=1920)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
          
          {/* Slide 3 - Si Funksionon */}
          <div 
            className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === 2 ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            style={{ background: 'rgba(51, 51, 51, 0.95)' }}
          >
            <div 
              className="absolute inset-0 opacity-30"
              style={{
                backgroundImage: 'url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            />
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 z-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        {/* Slide Content */}
        <div className="container-custom relative z-20 pt-20">
          {/* Slide 1 Content - Karikim i Shpejtë */}
          <div className={`transition-all duration-700 ${currentSlide === 0 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 absolute'}`}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
                  <span className="w-2 h-2 rounded-full bg-lime-400 animate-pulse" />
                  <span className="text-sm font-medium text-blue-100">Karikim i shpejtë dhe i sigurtë për Automjetin Tuaj Elektrik</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-bold font-['Inter_Tight'] tracking-tight leading-none mb-6">
                  Energji për<br />
                  <span className="text-lime-400">Lëvizshmëri</span>
                </h1>
                
                <p className="text-lg md:text-xl text-blue-100 leading-relaxed mb-10 max-w-lg">
                  AlbMove ofron rrjetin më të gjerë të stacioneve të karikimit për automjete elektrike në Shqipëri. 
                  Karikim i shpejtë, i sigurt dhe i përballueshëm.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Link to="/stations">
                    <Button className="bg-lime-400 hover:bg-lime-500 text-slate-900 rounded-full px-8 py-6 font-semibold text-lg">
                      Gjej Stacionin
                      <MapPin className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/services">
                    <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold text-lg">
                      Shërbimet Tona
                    </Button>
                  </Link>
                </div>
              </div>
              
              <div className="relative hidden lg:block">
                <img 
                  src="/images/hero-station-card.png"
                  alt="AlbMove Charging Station with EV"
                  className="rounded-3xl shadow-2xl w-full h-auto object-cover aspect-[4/3]"
                  data-testid="hero-station-image"
                />
                <div className="absolute -bottom-6 -left-6 glass bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
                      <Zap className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{stats?.total_stations || '8'}+</p>
                      <p className="text-sm text-slate-500">Stacione Aktive</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Slide 2 Content - Shkarko Aplikacionin */}
          <div className={`transition-all duration-700 ${currentSlide === 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 absolute'}`}>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Mockup majtas */}
              <div className="relative hidden lg:flex justify-center items-center min-h-[450px]">
                {/* Samsung mockup - mbrapa */}
                <img 
                  src="/images/mockups/samsung-mockup.png"
                  alt="AlbMove App - Android"
                  className="w-56 h-auto drop-shadow-2xl absolute left-0 top-4 -rotate-6"
                />
                {/* iPhone mockup - para */}
                <img 
                  src="/images/mockups/iphone-mockup.png"
                  alt="AlbMove App - iPhone"
                  className="w-60 h-auto drop-shadow-2xl relative z-10 translate-x-20 rotate-3"
                />
              </div>
              
              {/* Teksti djathtas */}
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
                  <Smartphone className="w-4 h-4 text-slate-800" />
                  <span className="text-sm font-medium text-slate-800">Aplikacioni Zyrtar i AlbMove</span>
                </div>
                
                <h1 className="text-5xl md:text-7xl font-bold font-['Inter_Tight'] tracking-tight leading-none mb-6">
                  Karikimi në<br />
                  <span className="text-slate-900">Majë të Gishtave</span>
                </h1>
                
                <p className="text-lg md:text-xl text-slate-800 leading-relaxed mb-10 max-w-lg">
                  Shkarkoni aplikacionin AlbMove dhe menaxhoni karikimin e automjetit tuaj nga çdo vend. 
                  Gjeni stacionet, nisni karikimin dhe paguani me lehtësi.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://apps.apple.com/hr/app/albmove/id6759310357"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-6 py-4 font-medium hover:bg-gray-900 transition-all hover:scale-105"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-70">Shkarko në</p>
                      <p className="text-lg font-semibold">App Store</p>
                    </div>
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.albmove.client&hl=sq"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-black text-white rounded-xl px-6 py-4 font-medium hover:bg-gray-900 transition-all hover:scale-105"
                  >
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-70">Merreni në</p>
                      <p className="text-lg font-semibold">Google Play</p>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Slide 3 Content - Si Funksionon */}
          <div className={`transition-all duration-700 ${currentSlide === 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8 absolute'}`}>
            <div className="text-white text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-8">
                <Play className="w-4 h-4 text-[#b0d33a]" />
                <span className="text-sm font-medium text-gray-200">Karikimi në 3 Hapa të Thjeshtë</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold font-['Inter_Tight'] tracking-tight leading-none mb-12">
                Si <span className="text-[#b0d33a]">Funksionon</span>?
              </h1>
              
              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-colors">
                  <div className="w-16 h-16 rounded-2xl bg-[#b0d33a] text-slate-900 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    1
                  </div>
                  <h3 className="text-xl font-bold mb-3">Gjeni Stacionin</h3>
                  <p className="text-gray-300">Hapni aplikacionin dhe gjeni stacionin më të afërt me hartën interaktive.</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-colors">
                  <div className="w-16 h-16 rounded-2xl bg-[#b0d33a] text-slate-900 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    2
                  </div>
                  <h3 className="text-xl font-bold mb-3">Lidh Konektorin</h3>
                  <p className="text-gray-300">Zgjidhni konektorin e duhur (CCS, GB/T, Type 2) dhe lidheni me automjetin.</p>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 hover:bg-white/20 transition-colors">
                  <div className="w-16 h-16 rounded-2xl bg-[#b0d33a] text-slate-900 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    3
                  </div>
                  <h3 className="text-xl font-bold mb-3">Nisni Karikimin</h3>
                  <p className="text-gray-300">Skanoni QR kodin ose nisni nga aplikacioni dhe paguani automatikisht.</p>
                </div>
              </div>
              
              <Link to="/stations" className="mt-12 inline-block">
                <Button className="bg-[#b0d33a] text-slate-900 hover:bg-[#9bc22e] rounded-full px-8 py-6 font-semibold text-lg">
                  Gjej Stacionin Tani
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Slider Navigation */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 flex items-center gap-4">
          <button 
            onClick={prevSlide}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex gap-2">
            {[0, 1, 2].map((index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === index ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
          
          <button 
            onClick={nextSlide}
            className="w-10 h-10 rounded-full bg-white/20 backdrop-blur text-white flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>
      
      {/* Stats Section */}
      <section data-testid="stats-section" className="py-16 bg-white border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: MapPin, value: stats?.total_stations || '8', suffix: '+', label: 'Stacione' },
              { icon: Battery, value: stats?.total_chargers || '25', suffix: '+', label: 'Karikues' },
              { icon: Building2, value: stats?.cities_covered || '8', suffix: '', label: 'Qytete' },
              { icon: Zap, value: stats?.total_energy_delivered || '1250', suffix: '+', label: 'MWh Energji' }
            ].map((stat, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div className="flex items-center justify-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center flex-shrink-0">
                    <stat.icon className="w-6 h-6 text-lime-500" />
                  </div>
                  <div>
                    <p className="text-3xl md:text-4xl font-bold text-blue-600 font-['Inter_Tight'] stat-number">
                      {stat.value}{stat.suffix}
                    </p>
                    <p className="text-slate-500 text-sm">{stat.label}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section data-testid="features-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
                Pse AlbMove?
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
                Eksperiencë e Plotë Karikimi
              </h2>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <div 
                  data-testid={`feature-card-${index}`}
                  className="feature-card group h-full"
                >
                  <div className={`w-14 h-14 rounded-2xl ${feature.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 font-['Inter_Tight'] mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section data-testid="how-it-works-section" className="section-padding" style={{ backgroundColor: '#1a3a2f' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-20">
              <h2 className="text-4xl md:text-5xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-4">
                Si Funksionon?
              </h2>
              <p className="text-lg text-gray-400">
                Procesi i thjeshtë i karikimit me AlbMove
              </p>
            </div>
          </ScrollReveal>
          
          <div className="relative">
            {/* Horizontal connecting line */}
            <div className="hidden lg:block absolute top-[140px] left-[12%] right-[12%] h-0.5 bg-lime-400/40" />
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
              {[
                { 
                  step: '01', 
                  icon: Download, 
                  title: 'Shkarko Aplikacionin', 
                  desc: 'Instaloni aplikacionin AlbMove nga App Store ose Google Play'
                },
                { 
                  step: '02', 
                  icon: MapPin, 
                  title: 'Gjej Stacionin', 
                  desc: 'Përdorni hartën për të gjetur stacionin më të afërt'
                },
                { 
                  step: '03', 
                  icon: QrCode, 
                  title: 'Skano & Kariko', 
                  desc: 'Skanoni kodin QR dhe filloni karikimin'
                },
                { 
                  step: '04', 
                  icon: Wallet, 
                  title: 'Paguaj', 
                  desc: 'Pagesa automatike nga portofoli juaj dixhital'
                }
              ].map((item, index) => (
                <ScrollReveal key={index} animation="fade-up" delay={index * 150}>
                  <div className="relative text-center group">
                    {/* Step Number - Large background */}
                    <div className="text-7xl md:text-8xl font-bold text-lime-400/15 font-['Inter_Tight'] leading-none select-none">
                      {item.step}
                    </div>
                    
                    {/* Icon Container with connecting elements */}
                    <div className="relative -mt-8 mb-8">
                      {/* Left connector dot */}
                      {index > 0 && (
                        <div className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full">
                          <ChevronRight className="w-6 h-6 text-lime-400/60 animate-pulse" />
                        </div>
                      )}
                      
                      {/* Icon pill shape */}
                      <div className="w-20 h-28 rounded-full bg-lime-400 flex items-center justify-center mx-auto shadow-lg shadow-lime-400/20 group-hover:scale-110 group-hover:shadow-lime-400/40 transition-all duration-300 cursor-pointer">
                        <item.icon className="w-9 h-9 text-[#2568b0]" strokeWidth={1.5} />
                      </div>
                    </div>
                    
                    {/* Title */}
                    <h3 className="text-xl font-semibold text-white font-['Inter_Tight'] mb-3 group-hover:text-lime-400 transition-colors">
                      {item.title}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-gray-400 text-sm leading-relaxed max-w-[220px] mx-auto">
                      {item.desc}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Charger Types Section - New Design */}
      <section data-testid="charger-types-section" className="section-padding" style={{ backgroundColor: '#e8f4f8' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                Llojet e Karikuesve
              </h2>
              <p className="text-lg text-slate-500">
                AC dhe DC për çdo nevojë
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* AC Charger Card */}
            <ScrollReveal animation="fade-right">
              <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 h-full">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Plug className="w-7 h-7 text-[#2568b0]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 font-['Inter_Tight'] mb-2">
                      Karikues AC
                    </h3>
                    <p className="text-slate-500 leading-relaxed">
                      Ideale për karikim të ngadaltë gjatë natës ose në punë.
                      Fuqia: 3.7kW - 22kW. Koha e karikimit: 4-8 orë.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
                    <Battery className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Fuqia: 3.7kW - 22kW</span>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-4 py-2">
                    <Clock className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-700">Koha: 4-8 orë</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            
            {/* DC Fast Charger Card */}
            <ScrollReveal animation="fade-left" delay={150}>
              <div className="bg-[#2568b0] rounded-3xl p-8 shadow-lg h-full">
                <div className="flex items-start gap-5 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-7 h-7 text-lime-400" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white font-['Inter_Tight'] mb-2">
                      Karikues DC (Fast Charging)
                    </h3>
                    <p className="text-blue-100 leading-relaxed">
                      Për karikim të shpejtë gjatë udhëtimit. Fuqia: 80kW - 240kW. Koha e karikimit: 15-30 minuta.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2">
                    <Battery className="w-4 h-4 text-slate-900" />
                    <span className="text-sm font-semibold text-slate-900">Fuqia: 80kW - 240kW</span>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2">
                    <Clock className="w-4 h-4 text-white" />
                    <span className="text-sm font-medium text-white">Koha: 15-30 min</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* App Section */}
      <section 
        data-testid="app-section" 
        className="section-padding text-white overflow-hidden relative"
        style={{ 
          backgroundImage: `linear-gradient(135deg, rgba(21, 101, 192, 0.85) 0%, rgba(13, 71, 161, 0.92) 100%), url('/images/app-section-bg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <ScrollReveal animation="fade-right" className="order-2 lg:order-1">
              <div className="relative flex items-center justify-center min-h-[500px]">
                {/* Samsung mockup - mbrapa */}
                <img 
                  src="/images/mockups/samsung-mockup.png"
                  alt="AlbMove Samsung app"
                  className="absolute w-[55%] max-w-[280px] -translate-x-[35%] translate-y-6 drop-shadow-2xl z-0"
                  data-testid="app-mockup-samsung"
                />
                {/* iPhone mockup - para */}
                <img 
                  src="/images/mockups/iphone-mockup.png"
                  alt="AlbMove iPhone app"
                  className="relative w-[55%] max-w-[280px] translate-x-[35%] drop-shadow-2xl z-10"
                  data-testid="app-mockup-iphone"
                />
              </div>
            </ScrollReveal>
            
            <ScrollReveal animation="fade-left" delay={200} className="order-1 lg:order-2">
              <div>
                <span className="inline-block text-sm font-medium uppercase tracking-widest text-lime-400 mb-4">
                  Aplikacioni AlbMove
                </span>
                <h2 className="text-4xl md:text-5xl font-semibold font-['Inter_Tight'] tracking-tight mb-6">
                  Karikimi në Majë të Gishtave
                </h2>
                <p className="text-lg text-blue-100 leading-relaxed mb-8">
                  Shkarkoni aplikacionin AlbMove dhe menaxhoni të gjithë eksperiencën tuaj të karikimit 
                  direkt nga telefoni. I disponueshëm për iOS dhe Android.
                </p>
                
                <ul className="space-y-4 mb-10">
                  {[
                    { icon: MapPin, text: 'Gjeni stacionet më të afërta në hartë' },
                    { icon: Zap, text: 'Nisni dhe ndaloni karikimin me një klikim' },
                    { icon: CreditCard, text: 'Pagesë e sigurt dhe e shpejtë' },
                    { icon: Clock, text: 'Historik i plotë i karikimeve' }
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-lime-400/20 flex items-center justify-center">
                        <item.icon className="w-5 h-5 text-lime-400" />
                      </div>
                      <span className="text-blue-100">{item.text}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="flex flex-wrap gap-4">
                  <a 
                    href="https://apps.apple.com/hr/app/albmove/id6759310357"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="app-store-link"
                    className="inline-flex items-center gap-3 bg-white text-slate-900 rounded-xl px-6 py-3 font-medium hover:bg-lime-100 transition-colors"
                  >
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-70">Shkarko në</p>
                      <p className="font-semibold">App Store</p>
                    </div>
                  </a>
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.albmove.client"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="play-store-link"
                    className="inline-flex items-center gap-3 bg-white text-slate-900 rounded-xl px-6 py-3 font-medium hover:bg-lime-100 transition-colors"
                  >
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                    </svg>
                    <div className="text-left">
                      <p className="text-xs opacity-70">Merreni në</p>
                      <p className="font-semibold">Google Play</p>
                    </div>
                  </a>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* RFID Section */}
      <section data-testid="rfid-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <ScrollReveal animation="zoom-in">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-blue-100 mb-8">
                <CreditCard className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight mb-6">
                Karta RFID AlbMove
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-10 max-w-2xl mx-auto">
                Alternativa e përsosur për ata që preferojnë një metodë më tradicionale. 
                Thjesht prekni kartën në karikues dhe filloni karikimin menjëherë.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: Shield, title: 'E Sigurt', desc: 'Teknologji e enkriptuar' },
                  { icon: Globe, title: 'Universale', desc: 'Funksionon kudo' },
                  { icon: Users, title: 'Për Të Gjithë', desc: 'Individë dhe biznese' }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 border border-slate-200/50">
                    <item.icon className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                    <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
      
      {/* Operators Section */}
      <section data-testid="operators-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal animation="fade-right">
              <div>
                <div className="inline-flex items-center gap-2 bg-blue-100 rounded-full px-4 py-2 mb-6">
                  <Server className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">OCPP 1.6J / 2.0.1</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                  Për Operatorët
                </h2>
                <p className="text-lg text-slate-500 mb-6">
                  Platforma profesionale për menaxhimin e stacioneve
                </p>
                
                <p className="text-slate-600 leading-relaxed mb-8">
                  AlbMove ofron një platformë të avancuar për operatorët që dëshirojnë të instalojnë dhe 
                  menaxhojnë stacione karikimi. Përfitoni nga teknologjia OCPP, monitorimi real-time, 
                  dhe integrimi i plotë me sistemin tonë.
                </p>
                
                <ul className="space-y-4">
                  {[
                    'Paneli i kontrollit real-time',
                    'Raporte të detajuara',
                    'API dhe WebSocket live',
                    'Suport teknik 24/7'
                  ].map((item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-lime-400 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-slate-900" />
                      </div>
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
            
            <ScrollReveal animation="fade-left" delay={200}>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1759731578354-73227161a245?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxBbGJhbmlhJTIwVGlyYW5hJTIwYXJjaGl0ZWN0dXJlJTIwbW9kZXJufGVufDB8fHx8MTc3MDk0Njg5NHww&ixlib=rb-4.1.0&q=85"
                  alt="Tiranë - Piramida"
                  className="rounded-3xl shadow-xl w-full"
                  data-testid="operators-image"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-lg border border-slate-100">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Real-time monitoring</p>
                      <p className="text-2xl font-bold text-slate-900 font-['Inter_Tight']">24/7</p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* Wallet System Section */}
      <section data-testid="wallet-section" className="section-padding" style={{ backgroundColor: '#e8f4e8' }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal animation="fade-right">
              {/* Wallet Card Mockup */}
              <div className="bg-white rounded-3xl p-8 shadow-xl max-w-md mx-auto">
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-slate-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">AlbMove Wallet</p>
                      <p className="font-semibold text-slate-900">Balance</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-blue-600 font-['Inter_Tight']">2,500</p>
                    <p className="text-sm text-slate-500">Credits (ALL)</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-blue-500" />
                      <span className="text-slate-700">Charging Session</span>
                    </div>
                    <span className="text-red-500 font-semibold">-450 ALL</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-lime-500" />
                      <span className="text-slate-700">Top Up</span>
                    </div>
                    <span className="text-lime-600 font-semibold">+1,000 ALL</span>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal animation="fade-left" delay={200}>
              <div>
                <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
                  <CreditCard className="w-4 h-4 text-slate-900" />
                  <span className="text-sm font-semibold text-slate-900">1 Credit = 1 Lek</span>
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                  Sistemi i Portofolit
                </h2>
                <p className="text-lg text-slate-500 mb-6">
                  Pagesë e lehtë dhe transparente
                </p>
                
                <p className="text-slate-600 leading-relaxed">
                  Me sistemin tonë të portofolit dixhital, 1 Credit = 1 Lek. Mund të mbushni portofolit tuaj 
                  me kartë krediti, transfertë bankare, ose në pikat e autorizuara. Çdo sesion karikimi 
                  regjistrohet me çmimin aktual (dynamic pricing) dhe fiskalizohet automatikisht.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
      
      {/* EV Infrastructure Section - 2x2 Grid */}
      <section data-testid="infrastructure-section" className="section-padding bg-white">
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                Infrastrukturë EV
              </h2>
              <p className="text-lg text-slate-500">
                Zgjidhje karikimi për çdo lloj biznesi
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                num: '01',
                icon: Truck,
                title: 'Menaxhim Flotash',
                subtitle: 'Optimizoni operacionet',
                description: 'Karikimi i centralizuar për flotën tuaj të automjeteve elektrike. Ulni kostot operative dhe maksimizoni efikasitetin me monitorim në kohë reale.',
                image: 'https://images.unsplash.com/photo-1761491713025-a7e66bf9427e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHx3aGl0ZSUyMGZsZWV0JTIwY2FycyUyMHBhcmtpbmclMjBsb3QlMjBidXNpbmVzc3xlbnwwfHx8fDE3NzA5NDcyMDJ8MA&ixlib=rb-4.1.0&q=85',
                imageAlt: 'Flotë makinash të bardha të parkuara',
                link: '/biznese/flota'
              },
              {
                num: '02',
                icon: Store,
                title: 'Karikim për Venue',
                subtitle: 'Tërheqni më shumë klientë',
                description: 'Ofroni karikues EV në restorantin, qendrën tregtare ose biznesin tuaj. Klientët modernë zgjedhin vende që ofrojnë karikim.',
                image: 'https://images.unsplash.com/photo-1758670496782-cb11b7b56675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxzaG9wcGluZyUyMG1hbGwlMjBlbnRyYW5jZSUyMG1vZGVybiUyMHJldGFpbHxlbnwwfHx8fDE3NzA5NDcyMDN8MA&ixlib=rb-4.1.0&q=85',
                imageAlt: 'Qendër tregtare moderne',
                link: '/biznese/venue'
              },
              {
                num: '03',
                icon: Hotel,
                title: 'Hotele & Turizëm',
                subtitle: 'Shërbim premium',
                description: 'Karikimi EV si amenity premium për mysafirët tuaj. Tërheqni udhëtarë të vetëdijshëm për mjedisin dhe krijoni të ardhura shtesë.',
                image: 'https://images.unsplash.com/photo-1762781838321-444256608b2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwyfHxyZXNvcnQlMjBob3RlbCUyMHBhbG0lMjB0cmVlcyUyMHdhbGt3YXklMjBzdW5ueXxlbnwwfHx8fDE3NzA5NDcyMDR8MA&ixlib=rb-4.1.0&q=85',
                imageAlt: 'Resort me palma',
                link: '/biznese/mikpritje'
              },
              {
                num: '04',
                icon: TreePine,
                title: 'Infrastrukturë Publike',
                subtitle: 'Mobilitet i qëndrueshëm',
                description: 'Instaloni karikues në hapësira publike dhe mbështetni tranzicionin drejt transportit të pastër. Ideal për bashki dhe organizata.',
                image: 'https://images.unsplash.com/photo-1767122230347-03e29dbf06ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwdWJsaWMlMjBwYXJrJTIwY2l0eSUyMGdyZWVuJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzcwOTQ3MjA1fDA&ixlib=rb-4.1.0&q=85',
                imageAlt: 'Park publik i gjelbër',
                link: '/biznese/publike'
              }
            ].map((item, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 100}>
                <Link 
                  to={item.link}
                  className="group block bg-slate-50 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300"
                >
                  <div className="p-8">
                    <div className="flex items-start gap-4 mb-4">
                      <span className="text-5xl font-bold text-blue-600/20 font-['Inter_Tight'] leading-none">{item.num}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                            <item.icon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] group-hover:text-blue-600 transition-colors">{item.title}</h3>
                        </div>
                        <span className="inline-block text-sm font-medium text-lime-600 mb-3">{item.subtitle}</span>
                        <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 font-medium text-sm mt-4">
                      <span>Mëso më shumë</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section data-testid="faq-section" className="section-padding" style={{ backgroundColor: '#e8f4f0' }}>
        <div className="container-custom">
          <ScrollReveal animation="fade-up">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
                Pyetje të Shpeshta
              </h2>
              <p className="text-lg text-slate-500">
                Përgjigje për pyetjet tuaja
              </p>
            </div>
          </ScrollReveal>
          
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: 'Si mund të filloj të përdor AlbMove?',
                answer: 'Shkarkoni aplikacionin AlbMove nga App Store ose Google Play, krijoni një llogari, mbushni portofolit tuaj dhe jeni gati për të karikuar. Gjeni stacionin më të afërt në hartë dhe skanoni kodin QR për të filluar.'
              },
              {
                question: 'Çfarë lloj pagese pranoni?',
                answer: 'Pranojmë kartë krediti/debiti, transfertë bankare, dhe pagesa në pikat e autorizuara. Sistemi ynë i portofolit dixhital funksionon me credit-e ku 1 Credit = 1 Lek.'
              },
              {
                question: 'A mund të përdor karikuesit tuaj me çdo automjet elektrik?',
                answer: 'Po, stacionet tona janë të pajtueshme me të gjitha automjetet elektrike që përdorin standarded CCS, GB/T, dhe Type 2. Kontrolloni aplikacionin për të parë llojin e konektorit të disponueshëm në çdo stacion.'
              },
              {
                question: 'Si funksionon çmimi dinamik?',
                answer: 'Çmimi dinamik përshtatet sipas orës së ditës dhe kërkesës. Çmimet janë më të ulëta gjatë orëve jo-peak dhe më të larta gjatë orëve të pikut. Çmimi aktual shfaqet gjithmonë në aplikacion para fillimit të karikimit.'
              },
              {
                question: 'A jeni të fiskalizuar?',
                answer: 'Po, AlbMove është plotësisht i fiskalizuar sipas legjislacionit shqiptar. Çdo sesion karikimi gjeneron automatikisht faturë fiskale që dërgohet në email-in tuaj.'
              }
            ].map((faq, index) => (
              <ScrollReveal key={index} animation="fade-up" delay={index * 50}>
                <details className="group bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                  <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown className="w-5 h-5 text-slate-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-6 pb-6 pt-0 text-slate-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section data-testid="cta-section" className="section-padding bg-white">
        <div className="container-custom">
          <ScrollReveal animation="zoom-in">
            <div className="rounded-3xl p-12 md:p-16 text-center relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-lime-400/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full blur-3xl" />
              
              <div className="relative z-10">
                <h2 className="text-3xl md:text-5xl font-bold text-white font-['Inter_Tight'] mb-6">
                  Gati për të Filluar?
                </h2>
                <p className="text-lg text-blue-100 mb-10 max-w-xl mx-auto">
                  Bashkohuni me mijëra përdorues që kanë zgjedhur AlbMove për karikimin e automjeteve të tyre elektrike.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild
                    data-testid="cta-contact-button"
                    className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
                  >
                    <Link to="/contact">
                      Na Kontaktoni
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                  <Button 
                    asChild
                    data-testid="cta-stations-button"
                    variant="outline"
                    className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium"
                  >
                    <Link to="/stations">
                      Shiko Stacionet
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
